import { cn } from '@/lib/utils';
import { Logo } from '@/components/ui/Logo';

const ICON = { sm: 'h-8 w-8', md: 'h-12 w-12', lg: 'h-14 w-14' } as const;

export type BrandedLoadingProps = {
  message: string;
  iconSize?: keyof typeof ICON;
  /** stack: icon above text (default). inline: icon left of text for compact rows */
  layout?: 'stack' | 'inline';
  className?: string;
};

/**
 * Full-page and section loading UI: rotating Five mark + status message (matches Coach).
 */
export function BrandedLoading({
  message,
  iconSize = 'md',
  layout = 'stack',
  className,
}: BrandedLoadingProps) {
  return (
    <div
      className={cn(
        layout === 'inline' ? 'flex flex-row items-center gap-2' : 'flex flex-col items-center gap-4',
        className
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={message}
    >
      <Logo variant="icon" className={cn(ICON[iconSize], 'animate-spin shrink-0')} />
      <p
        className={cn(
          'text-muted-foreground',
          layout === 'inline' ? 'text-sm text-left' : 'text-center text-sm sm:text-base font-medium'
        )}
      >
        {message}
      </p>
    </div>
  );
}

export function BrandedLoadingScreen({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <BrandedLoading message={message} iconSize="md" />
    </div>
  );
}

export type BrandedSpinnerProps = {
  /** Tailwind size classes, e.g. h-3 w-3, h-4 w-4, h-5 w-5 */
  sizeClassName?: string;
  className?: string;
};

/** Rotating Five mark for buttons, table rows, and inline loading (same asset as Coach). */
export function BrandedSpinner({ sizeClassName = 'h-4 w-4', className }: BrandedSpinnerProps) {
  return (
    <span className={cn('inline-flex shrink-0', className)} aria-hidden>
      <Logo variant="icon" className={cn(sizeClassName, 'animate-spin')} />
    </span>
  );
}
